'use client'

import { ChevronsUpDown, LogOut } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar'
import { logout } from '../../modules/auth/logout'
import { NavigationLink } from '../navigation/types'
import { ClientSelector } from '../navigation/ClientSelector'
import Link from 'next/link'
import { Separator } from '@/components/ui/separator'
import { Small, XSmall } from '@/components/ui/typography'
import { Icon } from '../Icon'
import { LocalStorageKey } from '@/lib/storageConstants'

export function NavUser({
    user,
    client,
    menuItems = [],
    onClientChange,
}: {
    user: {
        name: string
        email: string
        avatar: string
    }
    client?: any
    menuItems?: NavigationLink[]
    onClientChange?: (clientHash: string) => void
}) {
    const { isMobile } = useSidebar()

    const handleLogout = () => {
        logout()
        window.localStorage.removeItem(LocalStorageKey.CLIENT_HASH)
        window.location.href = '/login'
    }

    const handleItemClick = (item: NavigationLink, e: React.MouseEvent) => {
        if (item.openInNewTab && item.href) {
            e.preventDefault()
            window.open(item.href, '_blank')
            return
        }
    }

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        >
                            <Avatar className="size-8 rounded-lg">
                                <AvatarImage
                                    src={user.avatar}
                                    alt={user.name}
                                />
                                <AvatarFallback className="rounded-lg">
                                    {user.name.charAt(0) || 'U'}
                                </AvatarFallback>
                            </Avatar>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-semibold">
                                    {user.name}
                                </span>
                                <span className="truncate text-xs">
                                    {user.email}
                                </span>
                            </div>
                            <ChevronsUpDown className="ml-auto size-4" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="max-h-[calc(100vh-20px)] w-64 overflow-auto rounded-lg"
                        side={isMobile ? 'bottom' : 'right'}
                        align="end"
                        sideOffset={8}
                    >
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 p-2 text-left text-sm">
                                <Avatar className="size-10 rounded-lg">
                                    <AvatarImage
                                        src={user.avatar}
                                        alt={user.name}
                                    />
                                    <AvatarFallback className="rounded-lg">
                                        {user.name.charAt(0) || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate text-base font-semibold">
                                        {user.name}
                                    </span>
                                    <XSmall className="truncate text-muted-foreground">
                                        {client?.name || 'No client selected'}
                                    </XSmall>
                                    <XSmall className="truncate text-muted-foreground">
                                        {user.email}
                                    </XSmall>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        <Separator className="my-1" />

                        {/* Client Selector */}
                        {onClientChange && (
                            <div className="px-2 py-1.5">
                                <Small className="mb-1.5 block text-xs font-medium uppercase text-muted-foreground">
                                    Account
                                </Small>
                                <ClientSelector
                                    client={client}
                                    onClientChange={onClientChange}
                                    className="w-full"
                                />
                            </div>
                        )}

                        {/* Menu Items */}
                        {menuItems.length > 0 && (
                            <>
                                <Separator className="my-1" />
                                <DropdownMenuGroup>
                                    {menuItems.map((item) => (
                                        <DropdownMenuItem
                                            key={item.value}
                                            asChild
                                            className="truncate px-2 py-1.5 text-sm"
                                        >
                                            <Link
                                                href={item.href}
                                                onClick={(e) =>
                                                    handleItemClick(item, e)
                                                }
                                                className="flex w-full items-center gap-2 truncate"
                                            >
                                                {item.icon && (
                                                    <Icon
                                                        type={item.icon as any}
                                                        color="currentColor"
                                                        width={16}
                                                        height={16}
                                                    />
                                                )}
                                                <span>{item.label}</span>
                                            </Link>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuGroup>
                            </>
                        )}

                        <Separator className="my-1" />

                        {/* Logout */}
                        <DropdownMenuItem
                            className="truncate px-2 py-1.5 text-sm text-destructive focus:text-destructive"
                            onClick={handleLogout}
                        >
                            <LogOut className="mr-2 size-4" />
                            <span>Logout</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    )
}
